import React, { useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Spinner } from 'components';
import { actions as applyActions } from 'coupon-common/src/modules/customerCoupons/apply';

import { RouteNames } from '../../../constants';
import styles from './CouponApply.module.scss';
import CouponApplyFailure from './CouponApplyFailure';
import CouponApplySuccess from './CouponApplySuccess';

/**
 * UPDATE: now dispatches the real customerCoupons/apply redux action
 * (ported from mobile's coupons/apply duck) instead of calling the
 * `applyCoupon` API function directly. That function is a thin wrapper
 * around `authApiCall`, a redux-saga generator — calling it outside saga
 * middleware never actually hits the network, it just resolves near-
 * instantly with an unstarted generator object. Same root cause as the
 * CouponAction.js reserve/redeem bug, fixed the same way: dispatch the
 * action, read fetching/data/error back from redux state, mirroring how
 * mobile's own CouponApply screen consumes `state.coupons.apply[id]`.
 *
 * Also dropped the CSSModule(...) wrap from the default export — this file
 * only ever used `className={styles.x}`, never `styleName`, so CSSModule
 * was a no-op here (same situation already fixed in ReservedCoupon.js).
 */
const CouponApply = () => {
  const { id: idParam } = useParams();
  const id = idParam ? Number(idParam) : null;
  const [searchParams] = useSearchParams();
  const payByPoints = searchParams.get('payByPoints') === 'true';
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { fetching, error, data } = useSelector(
    state => (id && state.customerCoupons.apply?.[id]) || { fetching: true, error: null, data: null },
  );

  useEffect(() => {
    if (id) {
      dispatch(applyActions.applyCoupon(id, payByPoints));
    }
  }, [id, payByPoints, dispatch]);

  const goToUsedCoupons = () => navigate(RouteNames.HOME);
  const goToRecommendedCoupons = () => navigate(RouteNames.HOME);

  if (fetching) {
    return (
      <div className={styles.page}>
        <div className={styles.spinnerWrap}>
          <Spinner show size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {error ? (
        <CouponApplyFailure error={error} goToRecommendedCoupons={goToRecommendedCoupons} />
      ) : (
        <CouponApplySuccess couponAutoAccept={data?.state} goToUsedCoupons={goToUsedCoupons} />
      )}
    </div>
  );
};

export default CouponApply;